import { EyeOff, Landmark, Lock, RotateCcw, ShieldCheck, type LucideIcon } from 'lucide-react';
import { PAYMENT_CARD_CLASS, type PaymentContact, type PaymentMethodKind } from '../utils/paymentDisplay';
import { ContactLine, IconBadge } from './PaymentUi';

interface ReassuranceBlockProps {
  methodKind: PaymentMethodKind;
  contact: PaymentContact;
  /** Délai de rétractation (contrats upsell uniquement). */
  retractionDays?: number | null;
  className?: string;
}

/**
 * Garanties de sécurité affichées au payeur : Stripe, 3D Secure, confidentialité
 * des coordonnées bancaires, mandat SEPA, rétractation, contact.
 */
export function ReassuranceBlock({ methodKind, contact, retractionDays, className = '' }: ReassuranceBlockProps) {
  const name = contact.name ?? 'votre professionnel';
  const holder = contact.onBehalfOf ? `${name} ni par ${contact.onBehalfOf}` : name;

  const items: Array<{ icon: LucideIcon; title: string; text: string }> = [
    {
      icon: Lock,
      title: 'Paiement 100 % sécurisé par Stripe',
      text: 'Connexion chiffrée et prestataire de paiement certifié PCI DSS de niveau 1.',
    },
  ];
  if (methodKind === 'card') {
    items.push({
      icon: ShieldCheck,
      title: 'Authentification 3D Secure par votre banque',
      text: 'Votre banque peut vous demander de confirmer le paiement depuis son application ou par SMS.',
    });
  }
  items.push({
    icon: EyeOff,
    title: `Vos coordonnées bancaires ne sont ni vues ni conservées par ${holder}`,
    text: 'Elles sont transmises directement et de manière chiffrée à Stripe.',
  });
  if (methodKind === 'sepa_mandate') {
    items.push({
      icon: Landmark,
      title: 'Mandat de prélèvement SEPA',
      text: 'Vous êtes informé avant chaque échéance et pouvez demander à votre banque le remboursement d’un prélèvement dans les 8 semaines suivant le débit.',
    });
  }
  if (methodKind === 'sepa_single') {
    items.push({
      icon: Landmark,
      title: 'Prélèvement SEPA',
      text: 'Vous pouvez demander à votre banque le remboursement d’un prélèvement dans les 8 semaines suivant le débit.',
    });
  }
  if (retractionDays != null && retractionDays > 0) {
    items.push({
      icon: RotateCcw,
      title: `Droit de rétractation de ${retractionDays} jours`,
      text: 'Vous pouvez changer d’avis dans ce délai, sans avoir à vous justifier.',
    });
  }

  return (
    <aside aria-label="Sécurité du paiement" className={`${PAYMENT_CARD_CLASS} tw-p-5 sm:tw-p-6 ${className}`}>
      <ul className="tw-mb-0 tw-list-none tw-space-y-4 tw-pl-0">
        {items.map((item) => (
          <li key={item.title} className="tw-flex tw-items-start tw-gap-3">
            <IconBadge icon={item.icon} size="sm" />
            <div className="tw-min-w-0">
              <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">{item.title}</p>
              <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-leading-relaxed tw-text-gray-600">{item.text}</p>
            </div>
          </li>
        ))}
      </ul>

      <ContactLine contact={contact} className="tw-mt-5 tw-border-t tw-border-slate-100 tw-pt-4" />
    </aside>
  );
}
